import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { countTotalRows } from "@/components/spatialAnalysis/catchmentDataSection/catchmentDataset";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { ECatchmentType, ERoadAccessMeasurement } from "@/types/spatial";

import { booleanValid } from "@turf/turf";

const useSpatialCatchment = () => {
  const [isDisabled, setIsDisabled] = useState(true); // Initialize as disabled

  const { isBVTDataForCatchmentEnabled } = useAppFeatures();
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );

  const {
    radiusData = [],
    catchmentType,
    placeCoordinates,
    roadAccessConfig
  } = catchmentSettingsTemp;

  const handleToggleDisable = useCallback(() => {
    const totalPOIs =
      countTotalRows(catchmentSettingsTemp?.datasetConfig?.datasets) ?? 0;
    const hasCoordinateData =
      (placeCoordinates && placeCoordinates?.length > 0) || totalPOIs > 0;

    // Polygon-typed catchment datasets define the catchment area themselves —
    // no radius/road access config is required, so the Save button is enabled
    // as soon as at least one polygon dataset is added.
    const datasetEntries = Object.values(
      catchmentSettingsTemp?.datasetConfig?.datasets ?? {}
    );
    const hasPolygonCatchmentDataset = datasetEntries.some(
      (entry) =>
        entry.dataset?.data?.spatial_aggregation_type === "polygon" &&
        (entry.rawDataset?.rows?.length ?? 0) > 0
    );
    if (isBVTDataForCatchmentEnabled && hasPolygonCatchmentDataset) {
      setIsDisabled(false);
      return;
    }

    const byRadiusValid =
      radiusData?.filter((item) => item.trim() !== "")?.length > 0;

    const byRoadAccessValid = roadAccessConfig?.routingType;

    const distanceValid =
      roadAccessConfig?.measurementType === ERoadAccessMeasurement.DISTANCE &&
      roadAccessConfig?.distances &&
      roadAccessConfig?.distances?.filter((item) => item.trim() !== "")
        ?.length > 0;

    const durationValid =
      roadAccessConfig?.measurementType === ERoadAccessMeasurement.DURATION &&
      roadAccessConfig?.durations &&
      roadAccessConfig?.durations?.filter((item) => item.trim() !== "")
        ?.length > 0;

    const hasValidIsochroneCoordinates = () =>
      !!roadAccessConfig?.isochroneData?.length &&
      roadAccessConfig.isochroneData.every(
        (iso) =>
          Array.isArray(iso.data) &&
          iso.data.every((featureCollection) =>
            featureCollection.features.every(booleanValid)
          )
      );

    if (
      (!hasCoordinateData && distanceValid) ||
      (!hasCoordinateData && durationValid)
    ) {
      setIsDisabled(true);
      return;
    }
    let value = true;
    if (catchmentType === ECatchmentType.BY_RADIUS) {
      value = !byRadiusValid; // Return the *opposite* of byRadiusValid
    } else if (catchmentType === ECatchmentType.BY_ROAD) {
      const hasValidCoordinates = hasValidIsochroneCoordinates();
      value = !(
        byRoadAccessValid &&
        (distanceValid || durationValid) &&
        hasValidCoordinates
      ); // Return the *opposite*
    }
    setIsDisabled(value);
  }, [
    catchmentSettingsTemp,
    placeCoordinates,
    radiusData,
    catchmentType,
    roadAccessConfig,
    isBVTDataForCatchmentEnabled
  ]);

  useEffect(() => {
    handleToggleDisable();
  }, [handleToggleDisable]);

  return { isDisabled };
};

export default useSpatialCatchment;
